/**
 * ============================================
 * DOWNLOAD DROPDOWN
 * ============================================
 *
 * Sectioned download menu:
 *   Desktop  - Windows .exe (system-tray monitor)
 *   Mobile   - Standard APK  (Cognito login, role-based)
 *            - Monitor APK   (no-login, monitor-only, distinct package id)
 *   Reports  - Player stats CSV (admin only; hidden for monitor role)
 *
 * Why fetch+blob instead of `<a download>`:
 *   In Firefox/Safari, a `<a target="_blank" download>` link to /downloads/...
 *   was being intercepted by the PWA service worker's navigateFallback and
 *   served the SPA index.html shell, so the browser saved a .html file
 *   instead of the binary. The new flow fetches the bytes explicitly,
 *   wraps them in a Blob URL, and triggers download via a synthetic link.
 *   The Vite PWA config now also navigateFallbackDenylists /downloads/* as
 *   a second line of defence, but the blob path also works around any
 *   stale service worker that's still cached on user devices.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Download,
  Monitor,
  Smartphone,
  FileSpreadsheet,
  ChevronDown,
  Eye,
  Loader2,
  AlertCircle,
  LogIn,
} from 'lucide-react';

interface DownloadDropdownProps {
  onExportCSV: () => void;
  // Monitor role: see app downloads (needed for native alarms) but NOT
  // the CSV/reports section (no player-stats access).
  hideReports?: boolean;
}

type Status = 'idle' | 'downloading' | 'error';

interface DownloadItem {
  id: string;
  href: string;
  filename: string;
  title: string;
  subtitle: string;
  badge?: string;
  badgeTone?: 'blue' | 'amber' | 'emerald';
  icon: React.ReactNode;
  accentIcon?: React.ReactNode;
}

/**
 * Programmatic download. Returns when the file is fully streamed to disk
 * (or rejects on network/HTTP failure). Avoids the PWA SW navigation
 * fallback bug that turned /downloads/*.apk into index.html on Firefox
 * and Safari.
 */
async function downloadFile(href: string, filename: string): Promise<void> {
  // cache: 'no-store' so a stale SW that ignored navigateFallbackDenylist
  // also can't serve a cached index.html for this URL.
  const response = await fetch(href, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} ${response.statusText}`);
  }
  // Sanity check: if we somehow still got HTML (stale SW + cache hit),
  // refuse rather than save .html on the user's disk.
  const ct = (response.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('text/html')) {
    throw new Error('Got HTML instead of the file. Reload the page and try again.');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Release the blob -- not synchronous, but well after the click has been
  // consumed by the browser's download manager.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Badge({ tone, children }: { tone: 'blue' | 'amber' | 'emerald'; children: React.ReactNode }) {
  const toneClasses: Record<typeof tone, string> = {
    blue:    'bg-blue-500/15 text-blue-400 border-blue-500/30',
    amber:   'bg-amber-500/15 text-amber-400 border-amber-500/30',
    emerald: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  };
  return (
    <span className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${toneClasses[tone]}`}>
      {children}
    </span>
  );
}

export function DownloadDropdown({ onExportCSV, hideReports = false }: DownloadDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [statuses, setStatuses] = useState<Record<string, Status>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDownload = useCallback(async (item: DownloadItem) => {
    setStatuses(prev => ({ ...prev, [item.id]: 'downloading' }));
    setErrors(prev => ({ ...prev, [item.id]: '' }));
    try {
      await downloadFile(item.href, item.filename);
      setStatuses(prev => ({ ...prev, [item.id]: 'idle' }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Download failed';
      setStatuses(prev => ({ ...prev, [item.id]: 'error' }));
      setErrors(prev => ({ ...prev, [item.id]: msg }));
    }
  }, []);

  // ---- Menu structure --------------------------------------------------
  const desktop: DownloadItem[] = [
    {
      id: 'win',
      href: '/downloads/LokkoOverseer.exe',
      filename: 'LokkoOverseer.exe',
      title: 'Windows Client',
      subtitle: 'System-tray monitor with loud alarms',
      icon: <Monitor className="h-5 w-5 text-blue-400" />,
    },
  ];

  const mobile: DownloadItem[] = [
    {
      id: 'apk',
      href: '/downloads/LokkoOverseer.apk',
      filename: 'LokkoOverseer.apk',
      title: 'Android — Standard',
      subtitle: 'Full app · Cognito login · role-based access',
      badge: 'Login',
      badgeTone: 'emerald',
      icon: <Smartphone className="h-5 w-5 text-emerald-400" />,
      accentIcon: <LogIn className="h-3 w-3" />,
    },
    {
      id: 'monitor-apk',
      href: '/downloads/LokkoMonitor.apk',
      filename: 'LokkoMonitor.apk',
      title: 'Android — Monitor',
      subtitle: 'No login · permanently signed in · monitor view only',
      badge: 'No login',
      badgeTone: 'blue',
      icon: <Eye className="h-5 w-5 text-blue-400" />,
    },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(o => !o)}
        className="flex items-center gap-1 p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="Downloads"
      >
        <Download className="h-5 w-5" />
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-[min(94vw,20rem)] rounded-xl bg-popover border border-border shadow-xl z-50 overflow-hidden">

          {/* ---- Desktop section ---- */}
          <Section title="Desktop">
            {desktop.map(item => (
              <DownloadRow
                key={item.id}
                item={item}
                status={statuses[item.id] ?? 'idle'}
                error={errors[item.id]}
                onClick={() => handleDownload(item)}
              />
            ))}
          </Section>

          <Divider />

          {/* ---- Mobile section ---- */}
          <Section
            title="Mobile (Android)"
            footnote="Both APKs install side-by-side -- different package IDs."
          >
            {mobile.map(item => (
              <DownloadRow
                key={item.id}
                item={item}
                status={statuses[item.id] ?? 'idle'}
                error={errors[item.id]}
                onClick={() => handleDownload(item)}
              />
            ))}
          </Section>

          {/* ---- Reports section (admin only) ---- */}
          {!hideReports && (
            <>
              <Divider />
              <Section title="Reports">
                <button
                  onClick={() => {
                    onExportCSV();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-muted transition-colors text-left"
                >
                  <FileSpreadsheet className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">Export CSV</p>
                    <p className="text-xs text-muted-foreground truncate">Player stats snapshot</p>
                  </div>
                </button>
              </Section>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function Section({
  title,
  footnote,
  children,
}: {
  title: string;
  footnote?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="p-2">
      <p className="text-[10px] font-semibold text-muted-foreground px-2 pt-1 pb-1.5 uppercase tracking-widest">
        {title}
      </p>
      {children}
      {footnote && (
        <p className="text-[10px] text-muted-foreground/70 px-2 pt-1.5 leading-snug">
          {footnote}
        </p>
      )}
    </div>
  );
}

function Divider() {
  return <div className="border-t border-border/60" />;
}

function DownloadRow({
  item,
  status,
  error,
  onClick,
}: {
  item: DownloadItem;
  status: Status;
  error?: string;
  onClick: () => void;
}) {
  const isBusy = status === 'downloading';
  const hasError = status === 'error';
  return (
    <button
      onClick={onClick}
      disabled={isBusy}
      className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-md transition-colors text-left ${
        isBusy ? 'opacity-70 cursor-wait' : 'hover:bg-muted'
      }`}
    >
      <div className="flex-shrink-0 mt-0.5">{item.icon}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium">{item.title}</p>
          {item.badge && item.badgeTone && (
            <Badge tone={item.badgeTone}>
              {item.accentIcon}
              {item.accentIcon && <span className="ml-0.5">{item.badge}</span>}
              {!item.accentIcon && item.badge}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground leading-snug truncate">
          {item.subtitle}
        </p>
        <p className="text-[10px] text-muted-foreground/60 font-mono mt-0.5 truncate">
          {item.filename}
        </p>
        {hasError && error && (
          <p className="text-[10px] text-red-400 mt-1 flex items-start gap-1 leading-snug">
            <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </p>
        )}
      </div>
      <div className="flex-shrink-0 mt-0.5">
        {isBusy ? (
          <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
        ) : (
          <Download className={`h-4 w-4 ${hasError ? 'text-red-400' : 'text-muted-foreground'}`} />
        )}
      </div>
    </button>
  );
}
